import React, { useState } from "react";
import KpiStrip from "./KpiStrip";
import Tag from "./Tag";
import { day, money, num } from "../format";

function claimText(c) {
  const lines = [
    c.title,
    `SKU: ${c.sku}`,
    `Units: ${c.units}`,
    `Event date: ${c.eventDate}`,
    ...Object.entries(c.evidence || {}).map(([k, v]) => `${k}: ${v}`),
  ];
  return lines.join("\n");
}

export default function MoneyRecovery({ data, currency, highlight }) {
  const [copied, setCopied] = useState(null);
  const k = data.kpis;

  const copy = (c) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(claimText(c)).then(() => {
        setCopied(c.id);
        setTimeout(() => setCopied(null), 2000);
      });
    }
  };

  return (
    <div>
      <p className="ia-lead">
        Each claim is tracked from discovery to payment. Red means the claim window closes within 7 days.
      </p>
      <KpiStrip
        items={[
          { label: "Window closing in 7 days", value: money(k.windowClosingSoon.value, currency), sub: `${k.windowClosingSoon.claims} to file today`, level: k.windowClosingSoon.claims ? "crit" : null },
          { label: "Ready, closing later", value: money(k.readyClosingLater.value, currency), sub: `${k.readyClosingLater.claims} claims; ${money(k.readyClosingLater.waitingValue, currency)} in waiting period` },
          { label: "Filed, awaiting Amazon", value: k.filedAwaitingAmazon, sub: null },
          { label: "Recovered this quarter", value: money(k.recoveredThisQuarter.value, currency), sub: `${k.recoveredThisQuarter.count} reimbursements`, level: "ok" },
        ]}
      />
      <div className="ia-card ia-scroll">
        <table className="ia-table">
          <thead>
            <tr>
              <th>Claim</th>
              <th>SKU</th>
              <th className="ia-num">Units</th>
              <th className="ia-num">Value</th>
              <th>Claim window</th>
              <th>Stage</th>
              <th>Next step</th>
            </tr>
          </thead>
          <tbody>
            {data.claims.length === 0 ? (
              <tr>
                <td colSpan={7} className="ia-empty">No open claims. Lost, damaged and short units appear here as soon as the reports show them.</td>
              </tr>
            ) : (
              data.claims.map((c) => (
                <tr key={c.id} className={`${c.level === "u" ? "ia-row--urgent" : ""}${highlight === c.id ? " ia-row--highlight" : ""}`}>
                  <td className="ia-strong">{c.title}</td>
                  <td>{c.sku}</td>
                  <td className="ia-num">{num(c.units)}</td>
                  <td className="ia-num" title={c.valueBasis === "price" ? "At selling price - add unit costs in Alert settings" : "At unit cost"}>
                    {money(c.value, currency)}
                    {c.valueBasis === "price" ? <small className="ia-muted"> at price</small> : null}
                  </td>
                  <td className="ia-window">
                    <div className="ia-progress">
                      <i style={{ width: `${c.windowPct}%` }} className={c.level === "u" ? "ia-bg--crit" : ""} />
                    </div>
                    <small className={c.level === "u" ? "ia-text--crit ia-strong" : "ia-muted"}>{c.windowText}</small>
                  </td>
                  <td>
                    <Tag level={c.stage.level}>{c.stage.label}</Tag>
                  </td>
                  <td>
                    {c.nextStep ? <div className="ia-strong">{c.nextStep}</div> : null}
                    <button type="button" className="ia-link" onClick={() => copy(c)}>
                      {copied === c.id ? "Copied" : "Copy claim details"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {data.recentReimbursements && data.recentReimbursements.length ? (
        <section className="ia-section">
          <h3 className="ia-subtitle">Reimbursements this quarter</h3>
          <div className="ia-card ia-scroll">
            <table className="ia-table ia-table--compact">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Reason</th>
                  <th>SKU</th>
                  <th className="ia-num">Units</th>
                  <th className="ia-num">Amount</th>
                  <th>Case</th>
                </tr>
              </thead>
              <tbody>
                {data.recentReimbursements.map((r) => (
                  <tr key={r.id}>
                    <td>{day(r.date)}</td>
                    <td>{(r.reason || "").replace(/_/g, " ")}</td>
                    <td>{r.sku}</td>
                    <td className="ia-num">{num(r.units)}</td>
                    <td className="ia-num">{money(r.amount, r.currency || currency)}</td>
                    <td>{r.caseId || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
